/**
 * Ridge patterns
 *
 * Loop, whorl and arch per digit from the ridge orientation field (Poincaré
 * index), with an honest `indeterminate` when the photograph does not resolve
 * ridges — which is nearly every phone photograph of a whole hand. A finger tip
 * is ~15 mm wide, ridges are ~0.5 mm apart: to see them at all you need ~150 px
 * across the tip, and the working image is 640 px across the entire hand.
 *
 * ⚠ **No print is ever stored or serialised; only the class letter.** The
 * orientation field is a local variable and is discarded on return.
 *
 * © Manish Jagdish Thatte
 */

import { gaussianBlur } from './image.js';

/** Minimum fingertip width, in pixels, at which ridges (~5 px period) can be resolved. */
export const MIN_TIP_WIDTH_PX = 150;

/**
 * Block-wise ridge orientation from the structure tensor.
 * Returns `{ cols, rows, theta: Float32Array (radians, 0..π), coherence: Float32Array }`.
 */
export function ridgeOrientation(gray, region = null, block = 8) {
  const g = gaussianBlur(gray, 1);
  const { width: w, height: h, data } = g;
  const x0 = Math.max(1, region?.x0 ?? 1); const y0 = Math.max(1, region?.y0 ?? 1);
  const x1 = Math.min(w - 1, region?.x1 ?? w - 1); const y1 = Math.min(h - 1, region?.y1 ?? h - 1);
  const cols = Math.floor((x1 - x0) / block); const rows = Math.floor((y1 - y0) / block);
  const theta = new Float32Array(cols * rows); const coherence = new Float32Array(cols * rows);
  for (let by = 0; by < rows; by++) for (let bx = 0; bx < cols; bx++) {
    let gxx = 0; let gyy = 0; let gxy = 0;
    for (let y = y0 + by * block; y < y0 + (by + 1) * block; y++) for (let x = x0 + bx * block; x < x0 + (bx + 1) * block; x++) {
      const gx = (data[y * w + x + 1] - data[y * w + x - 1]) / 2; const gy = (data[(y + 1) * w + x] - data[(y - 1) * w + x]) / 2;
      gxx += gx * gx; gyy += gy * gy; gxy += gx * gy;
    }
    // ridge direction is perpendicular to the dominant gradient
    const phi = 0.5 * Math.atan2(2 * gxy, gxx - gyy) + Math.PI / 2;
    theta[by * cols + bx] = ((phi % Math.PI) + Math.PI) % Math.PI;
    const tr = gxx + gyy;
    coherence[by * cols + bx] = tr > 1e-12 ? Math.hypot(gxx - gyy, 2 * gxy) / tr : 0;
  }
  return { cols, rows, theta, coherence };
}

function angDiff(a, b) {
  let d = a - b; while (d > Math.PI / 2) d -= Math.PI; while (d <= -Math.PI / 2) d += Math.PI;
  return d;
}

/**
 * Poincaré index at each interior cell: ≈ +½ core, ≈ −½ delta, ≈ +1 whorl centre.
 *
 * The path is the 16 cells on the perimeter of the 5×5 window. A 3×3 ring is too
 * tight: around a whorl centre consecutive samples are ~90° apart in orientation,
 * which is indistinguishable mod π from 0°, and the whorl reads as a loop.
 */
export function poincare({ cols, rows, theta }) {
  const R = 2; const ring = [];
  for (let i = -R; i < R; i++) ring.push([i, -R]);
  for (let i = -R; i < R; i++) ring.push([R, i]);
  for (let i = R; i > -R; i--) ring.push([i, R]);
  for (let i = R; i > -R; i--) ring.push([-R, i]);
  const pts = [];
  for (let y = R; y < rows - R; y++) for (let x = R; x < cols - R; x++) {
    let s = 0;
    for (let k = 0; k < ring.length; k++) {
      const a = ring[k]; const b = ring[(k + 1) % ring.length];
      s += angDiff(theta[(y + b[1]) * cols + x + b[0]], theta[(y + a[1]) * cols + x + a[0]]);
    }
    const idx = s / (2 * Math.PI);
    if (Math.abs(idx) > 0.3) pts.push({ x, y, index: idx });
  }
  return pts;
}

/** Merge neighbouring singular cells of like sign into one point each. */
function cluster(pts, dist = 3) {
  const out = [];
  for (const p of pts) {
    const c = out.find((q) => Math.sign(q.index) === Math.sign(p.index) && Math.hypot(q.x - p.x, q.y - p.y) <= dist);
    if (c) { c.n++; c.index += p.index; c.x += (p.x - c.x) / c.n; c.y += (p.y - c.y) / c.n; } else out.push({ ...p, n: 1 });
  }
  return out.map((c) => ({ x: c.x, y: c.y, index: c.index / c.n }));
}

/**
 * Classify a print from its orientation field.
 * Returns `{ class: 'loop'|'whorl'|'arch'|'indeterminate', letter, cores, deltas, reason? }`.
 * The letter is the only thing that may be kept.
 */
export function classifyPrint(orientation, { minCoherence = 0.3 } = {}) {
  const { coherence } = orientation;
  const mean = coherence.length ? coherence.reduce((a, b) => a + b, 0) / coherence.length : 0;
  const bad = (reason) => ({ class: 'indeterminate', letter: '?', cores: 0, deltas: 0, reason });
  if (orientation.cols < 5 || orientation.rows < 5) return bad('too-small');
  if (mean < minCoherence) return bad('ridges-not-resolved');
  const pts = cluster(poincare(orientation));
  const cores = pts.filter((p) => p.index > 0);
  const deltas = pts.filter((p) => p.index < 0);
  const strong = cores.filter((p) => p.index > 0.8).length;
  if (strong >= 1 || cores.length >= 2) return { class: 'whorl', letter: 'W', cores: cores.length, deltas: deltas.length };
  if (cores.length === 1) return { class: 'loop', letter: 'L', cores: 1, deltas: deltas.length };
  if (deltas.length === 0) return { class: 'arch', letter: 'A', cores: 0, deltas: 0 };
  return bad('ambiguous');
}

/**
 * Classify each digit, if and only if the pixels allow it.
 * `gray` should be the **full-resolution** luminance; `scale` is working/full (< 1).
 * On a downscaled whole-hand image every digit is `indeterminate` — the truthful answer.
 */
export function digitPrints(gray, landmarks, { scale = 1 } = {}) {
  const names = ['thumb', 'index', 'middle', 'ring', 'little'];
  return names.map((name, i) => {
    const tip = landmarks.tips[i];
    const widthPx = (landmarks.palmWidth * 0.2) / scale;                // a fingertip is ~ a fifth of the palm width
    if (widthPx < MIN_TIP_WIDTH_PX) return { digit: name, class: 'indeterminate', letter: '?', reason: 'resolution', widthPx: Math.round(widthPx) };
    const r = Math.round(widthPx * 0.6);
    const cx = Math.round(tip[0] / scale); const cy = Math.round(tip[1] / scale);
    const o = ridgeOrientation(gray, { x0: cx - r, y0: cy - r, x1: cx + r, y1: cy + r });
    const res = classifyPrint(o);
    return { digit: name, class: res.class, letter: res.letter, reason: res.reason ?? null, widthPx: Math.round(widthPx) };
  });
}
